import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AccountModel, SchwabClient, StandingInstruction } from 'src/app/core/clients/generated/client';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountWorkflowNavModel, SolicitationOptionModel } from '../shared/models/workflow.models';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalData } from 'src/app/shared/modals/confirmation-modal/modal-data.models';
import { RequestExitConfirmationData } from '../shared/constants/workflow-navigation.constants';
import { DistributionFormService } from 'src/app/portal/features/account-request-workflows/distribution-request/services/distribution-form.service';
import { fadeIn } from 'src/app/shared/constants/animations';
import { AccountUpdateStepEnum, RequestTypeEnum } from '../shared/enums/account-update.enum';
import { AllocationSleeveFormArray } from '../shared/models/allocation-form.models';
import { AccountRequestModalHeaderComponent } from '../shared/components/account-request-modal-header/account-request-modal-header.component';

@Component({
  selector: 'app-distribution-request',
  templateUrl: './distribution-request.component.html',
  providers: [DistributionFormService],
  animations: [fadeIn]
})
export class DistributionRequestComponent implements OnInit {

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DistributionRequestComponent>,
    private schwabClient: SchwabClient,
    public distributionFormService: DistributionFormService
  ) { }
  @Output() canceled = new EventEmitter();
  @Input() account?: AccountModel;
  @Input() workflow?: AccountWorkflowNavModel;
  @Input() solicitation?: SolicitationOptionModel;
  @ViewChild('header') header?:AccountRequestModalHeaderComponent;
  standingInstructions?: StandingInstruction[] = [];
  accountUpdateStepEnum = AccountUpdateStepEnum;
  requestTypeEnum = RequestTypeEnum;
  isSubmitting = false;
  isSubmitted = false;
  isComplete = false;
  loadingPage = false;
  submitCount = 0;
  crmRequestID?: number;
  form = this.distributionFormService.form;
  pages = this.distributionFormService.pages;

  ngOnInit(): void {
    if (this.account) {
      this.distributionFormService.account = this.account;
      this.distributionFormService.addSleeves(this.account);

      //adding blank option for Other to display in drop down lists
      const blankInstructionOption : StandingInstruction = {
        bankAccountNumber: '',
        bankName: '',
        direction: '',
        transactionType: 'ALL',
        displayValue: 'Other (New Account)',
        hasIaAuthority: false,
        // Carried by the contract since ACMTECH-1036 and meaningless here. They describe a
        // cheque's counterparty ADDRESS, which Schwab files on type-C standing instructions --
        // V1 drops those rows on the floor exactly as the API used to, and has no address book
        // to put them in. Stated as undefined rather than '' so nothing downstream mistakes an
        // empty string for an address line that happens to be blank.
        payeeName: undefined,
        address1: undefined,
        address2: undefined,
        address3: undefined,
        countryCode: undefined,
      };

      if (this.account?.schwabMasterAccount && this.account?.schwabFormattedAccount) {
        this.loadingPage = true;
        this.schwabClient.getAccountStandingInstructions(this.account?.investmentAccountID, this.account?.schwabMasterAccount, this.account?.schwabFormattedAccount).subscribe({
          next: results => {
            const outgoing = results.filter(x => x.direction === 'OUTGOING');
            this.standingInstructions = outgoing;
            this.standingInstructions?.push(blankInstructionOption);
            this.loadingPage = false;
          },
          error: () => {
            this.loadingPage = false;
          },
          complete: () => {
            this.loadingPage = false;
          },
        });
      } else {
        this.standingInstructions?.push(blankInstructionOption);
      }

      //remove validators of control if no results so it's not required.
      // if (this.standingInstructions === undefined || this.standingInstructions?.length === 0)
      //   this.form.controls.deliveryOptions.controls.standingInstruction.setValidators(null);
    }

    if (this.solicitation) {
      this.distributionFormService.isSolicited = this.solicitation?.isSolicited;
    }
  }

  get sleevesFormArray(): AllocationSleeveFormArray {
    return this.form.controls.allocationConfiguration.controls.sleeves as AllocationSleeveFormArray;
  }

  get showCustomHoldings(): boolean {
    return this.form.controls.allocationConfiguration.value.showCustomHoldings;
  }

  closeRequest(): void {
    if (this.isSubmitted && this.isComplete) {
      this.dialogRef.close(false);
      return;
    }

    const confirmationDialog = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      data: RequestExitConfirmationData
    });

    confirmationDialog.afterClosed().subscribe(res => {
      if (res) this.dialogRef.close(false);
      else return;
    });
  }

  cancelRequest(): void {
    const confirmationDialog = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      data: RequestExitConfirmationData
    });

    confirmationDialog.afterClosed().subscribe(res => {
      if (res) {
        this.canceled.emit();
      } else return;
    });
  }

  onSubmit(): void {
    if (this.account) {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
      } else {
        this.isSubmitting = true;
        this.distributionFormService.postRequest(this.account.investmentAccountID).subscribe({
          next: (result) => {
            //used to pass to complete page for survey
            this.crmRequestID = result;
          },
          error: () => {
            this.submitCount++;
            // for work around on sometimes the first request failing submitting to the crm.
            if (this.submitCount < 2){
              console.log('received error, retrying in 2 seconds');
              setTimeout(() =>{
                this.onSubmit();
              }, 2000);
            } else {
              this.isSubmitting = false;
              this.isSubmitted = true;
              this.isComplete = false;
              this.submitCount = 0;
            }
          },
          complete: () => {
            this.isSubmitting = false;
            this.isSubmitted = true;
            this.isComplete = true;
          }
        });
      }
    }
  }
}